import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { bpConfig } from './login/data';
import MosaicBackground from './login/MosaicBackground';
import HeroCarousel from './login/HeroCarousel';
import EnrollCard from './login/EnrollCard';
import AssessCard from './login/AssessCard';
import ScoreCard from './login/ScoreCard';
import TrackCard from './login/TrackCard';
import LoginPanel from './login/LoginPanel';
import BrandPillars from './login/BrandPillars';
import './login/loginAnimations.css';

const LoginPage: React.FC = () => {
  const { login, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'enterprise' | 'mssp'>('enterprise');
  const bp = useBreakpoint();
  const cfg = bpConfig[bp];

  const handleLogin = async () => {
    try {
      setError(null);
      await login();
    } catch (err) {
      setError('Login failed. Please try again.');
      console.error('Login error:', err);
    }
  };

  const handleToggle = () => {
    setMode(prev => (prev === 'enterprise' ? 'mssp' : 'enterprise'));
  };

  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const isStacked = isMobile || isTablet;

  const loginPanel = (
    <LoginPanel
      mode={mode}
      onToggle={handleToggle}
      onSignIn={handleLogin}
      isLoading={isLoading}
      error={error}
      breakpoint={bp}
    />
  );

  const heroSection = (
    <div className="flex flex-col relative overflow-y-auto" style={{ padding: cfg.heroPadding, scrollbarWidth: 'thin' }}>
      {/* Brand lockup — hidden on stacked layouts where login panel already shows logo */}
      {!isStacked && (
        <div
          className="flex items-center gap-[14px] mb-7"
          style={{ animation: 'fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.3s both' }}
        >
          <img
            src="/anchor-mark-96.png"
            alt=""
            className="w-[34px] h-[34px] object-contain"
            style={{ imageRendering: 'pixelated' }}
          />
          <span className="font-mono text-[13px] font-semibold tracking-[0.15em] text-white/80 uppercase">
            Data<span className="text-anchor-teal">_</span>Anchor
          </span>
        </div>
      )}

      {/* Headline */}
      <h1
        className="font-nippo font-bold leading-[1.1] tracking-[-0.015em] max-w-[620px] mb-6"
        style={{
          fontSize: `${cfg.headlineSize}px`,
          animation: 'fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) 0.45s both',
        }}
      >
        Your business already has a security score.<br />
        <span className="text-anchor-teal">You haven&rsquo;t seen it. Someone else has.</span>
      </h1>

      {/* Carousel + tabs */}
      <HeroCarousel breakpoint={bp}>
        <EnrollCard mode={mode} breakpoint={bp} />
        <AssessCard mode={mode} breakpoint={bp} />
        <ScoreCard mode={mode} breakpoint={bp} />
        <TrackCard mode={mode} breakpoint={bp} />
      </HeroCarousel>

      {/* Brand pillars */}
      <BrandPillars breakpoint={bp} />
    </div>
  );

  return (
    <div className={`relative bg-anchor-navy font-dm text-white ${isStacked ? 'min-h-screen overflow-y-auto' : 'h-screen overflow-hidden'}`}>
      <MosaicBackground breakpoint={bp} />

      <div
        className={`relative z-10 grid ${isStacked ? 'min-h-screen' : 'h-screen'}`}
        style={{ gridTemplateColumns: cfg.gridCols }}
      >
        {isStacked ? (
          <>
            {loginPanel}
            {heroSection}
          </>
        ) : (
          <>
            {heroSection}
            {loginPanel}
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
